import * as RadixCollapsible from '@radix-ui/react-collapsible'
import { Divider } from '@sunwebgroup/uds-ui/divider'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { DepositDetails } from '@/scopes/payment/components/DepositDetails'
import { formatPaymentDueDateLabel } from '@/scopes/payment/components/PaymentReminders/formatPaymentDueDate'
import {
  FullPaymentReminderRow,
  RemainingDepositRow,
} from '@/scopes/payment/components/PaymentReminders/PaymentReminders'
import { PaymentSection } from '@/scopes/payment/components/PaymentSection'
import { usePaymentContext } from '@/scopes/payment/domain/PaymentContext'
import { isFullOnlyPayment } from '@/scopes/payment/domain/policies/fullOnlyPayment'
import { getPaymentAmountsSorter } from '@/scopes/payment/domain/policies/paymentAmountsOrder'
import { AmountType, AmountTypeValues, PaymentAmount, PaymentScopePageValues } from '@/scopes/payment/domain/types'
import styles from './PaymentAmounts.module.css'

interface PaymentAmountsProps {
  paymentAmounts: PaymentAmount[]
  selectedAmountType: AmountType | ''
  onSelectedAmountTypeChange: (amountType: AmountType) => void
  customDepositAmount: number | null
  onCustomDepositAmountChange: (amount: number) => void
  depositDueDate?: string
  fullPaymentDueDate?: string
  id?: string
}

const DEPOSIT_AMOUNT_TRANSLATION_KEY = {
  default: 'payment_scope_deposit_amount_label',
  customPartial: 'payment_scope_custom_partial_amount_label',
} as const

const PAYMENT_AMOUNTS_SECTION_COPY_TRANSLATION_KEY = {
  withDeposit: {
    title: {
      id: 'payment_scope_amounts_title',
      defaultMessage: 'Pay in full or spread the cost',
    },
    description: {
      id: 'payment_scope_amounts_description',
      defaultMessage:
        'Choose how you\'d like to pay for your trip. Spreading the cost lets you secure your booking with a deposit today and pay the rest closer to your departure date.',
    },
  },
  fullOnly: {
    title: {
      id: 'payment_scope_amounts_full_only_title',
      defaultMessage: 'Payment',
    },
    description: {
      id: 'payment_scope_amounts_full_only_description',
      defaultMessage:
        'Only full payment is available at this time. Choose a payment method below to pay the outstanding amount.',
    },
  },
} as const

function formatPaymentAmountLabel(type: string) {
  return type.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function filterTranslationAmountKeys(key: AmountType, isCustomPartialOnly: boolean): string | undefined {
  const mapping: Record<AmountType, string> = {
    full: 'payment_scope_full_amount_label',
    deposit: isCustomPartialOnly
      ? DEPOSIT_AMOUNT_TRANSLATION_KEY.customPartial
      : DEPOSIT_AMOUNT_TRANSLATION_KEY.default,
    minDeposit: 'payment_scope_min_deposit_amount_label',
  }
  return mapping[key]
}

export function PaymentAmounts({
  paymentAmounts,
  selectedAmountType,
  onSelectedAmountTypeChange,
  customDepositAmount,
  onCustomDepositAmountChange,
  depositDueDate,
  fullPaymentDueDate,
  id,
}: PaymentAmountsProps) {
  const intl = useIntl()
  const siteName = useConfiguration('siteName')
  const { scope } = usePaymentContext()
  const orderedPaymentAmounts = getPaymentAmountsSorter(siteName, scope)(paymentAmounts)
  const hasDepositAmount = paymentAmounts.some(amount => amount.type !== AmountTypeValues.FULL)
  const sectionCopy = hasDepositAmount
    ? PAYMENT_AMOUNTS_SECTION_COPY_TRANSLATION_KEY.withDeposit
    : PAYMENT_AMOUNTS_SECTION_COPY_TRANSLATION_KEY.fullOnly
  const sectionTitle = intl.formatMessage(sectionCopy.title)
  const sectionDescription = intl.formatMessage(sectionCopy.description)

  if (!orderedPaymentAmounts.length)
    return null

  if (isFullOnlyPayment(paymentAmounts)) {
    const totalAmountLabel
      = fullPaymentDueDate && scope !== PaymentScopePageValues.MANDATORY_PAYMENT
        ? intl.formatMessage(
            { id: 'payment_scope_full_amount_due_date_summary', defaultMessage: 'Total amount, {date}' },
            {
              date: formatPaymentDueDateLabel(intl, fullPaymentDueDate),
            },
          )
        : intl.formatMessage({ id: 'payment_scope_full_amount_summary', defaultMessage: 'Total amount' })

    return (
      <PaymentSection
        title={sectionTitle}
        description={sectionDescription}
        id={id}
        glyph="PaymentCardWithCoins"
        className={styles.fullOnlySection}
      >
        <ListItem variant="outlined" container="S" className={styles.fullAmountSummary}>
          <Typography as="p" variant="body-s-regular-tight" className={styles.summaryTitle}>
            {intl.formatMessage({ id: 'payment_scope_amount_summary_title', defaultMessage: 'Amount' })}
          </Typography>
          <dl className={styles.summaryRow}>
            <Typography as="dt" variant="body-m-regular-tight">
              {totalAmountLabel}
            </Typography>
            <Typography as="dd" variant="body-l-bold-snug" className={styles.summaryAmount}>
              {intl.formatCurrency(paymentAmounts[0].amount)}
            </Typography>
          </dl>
        </ListItem>
      </PaymentSection>
    )
  }

  const depositAmount = paymentAmounts.find(a => a.type === AmountTypeValues.DEPOSIT)?.amount
  const standardDepositAmount = paymentAmounts.find(
    a => a.type === AmountTypeValues.DEPOSIT && a.isDownPayment !== false,
  )?.amount

  const fullAmount = paymentAmounts.find(a => a.type === AmountTypeValues.FULL)?.amount
  const minimumDepositAmount = paymentAmounts.find(a => a.type === AmountTypeValues.MIN_DEPOSIT)?.amount
  const depositAllowsCustomAmount = paymentAmounts.find(a => a.type === AmountTypeValues.DEPOSIT)?.allowsCustomAmount ?? false
  const shouldShowDepositDetails
    = selectedAmountType === AmountTypeValues.DEPOSIT
      && depositAllowsCustomAmount
      && depositAmount !== undefined
      && fullAmount !== undefined

  return (
    <PaymentSection
      title={sectionTitle}
      description={sectionDescription}
      id={id}
      glyph="PaymentCardWithCoins"
    >
      <SelectableRadioButtonGroup
        aria-label={sectionTitle}
        value={selectedAmountType}
        onValueChange={value => onSelectedAmountTypeChange(value as AmountType)}
      >
        <div className={styles.amounts}>
          {orderedPaymentAmounts.map(({ type, amount, allowsCustomAmount, isDownPayment }) => {
            const isDepositCustomAmountEnabled = allowsCustomAmount
            const isCustomPartialOnly = isDownPayment === false
            const amountTextKey = filterTranslationAmountKeys(type, isCustomPartialOnly) || ''
            const formattedAmount = intl.formatCurrency(amount)
            const label = amountTextKey
              ? intl.formatMessage(
                  { id: amountTextKey, defaultMessage: `${formatPaymentAmountLabel(type)} ${formattedAmount}` },
                  { amount: formattedAmount },
                )
              : `${formatPaymentAmountLabel(type)} ${formattedAmount}`

            return (
              <SelectableListItem value={type} key={type} className={styles.amountListItem}>
                <div className={styles.amountHeader}>
                  <RadioButton
                    value={type}
                    className={styles.amountRadio}
                    aria-label={typeof label === 'string' ? label : undefined}
                    onClick={event => event.stopPropagation()}
                  />
                  <Typography variant="body-m-regular-tight" className={styles.amountListItemContent}>{label}</Typography>
                </div>
                {type === AmountTypeValues.DEPOSIT && isDepositCustomAmountEnabled && (
                  <RadixCollapsible.Root open={shouldShowDepositDetails}>
                    <RadixCollapsible.Content className={styles.collapsibleContent}>
                      {depositAmount !== undefined && fullAmount !== undefined && (
                        <DepositDetails
                          minimumDeposit={
                            paymentAmounts.find(a => a.type === AmountTypeValues.DEPOSIT)?.customPartialMinAmount
                            ?? depositAmount
                          }
                          totalPrice={fullAmount}
                          value={customDepositAmount}
                          onChange={onCustomDepositAmountChange}
                          dueDate={depositDueDate}
                          fullPaymentDueDate={fullPaymentDueDate}
                        />
                      )}
                    </RadixCollapsible.Content>
                  </RadixCollapsible.Root>
                )}
                {type === AmountTypeValues.MIN_DEPOSIT && (
                  <RadixCollapsible.Root open={selectedAmountType === AmountTypeValues.MIN_DEPOSIT}>
                    <RadixCollapsible.Content className={styles.collapsibleContent}>
                      {minimumDepositAmount !== undefined && (
                        <PaymentAmountDetails
                          minimumDepositAmount={minimumDepositAmount}
                          depositAmount={standardDepositAmount}
                          fullAmount={fullAmount}
                          dueDate={depositDueDate}
                          fullPaymentDueDate={fullPaymentDueDate}
                          amountType={type}
                        />
                      )}
                    </RadixCollapsible.Content>
                  </RadixCollapsible.Root>
                )}
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
    </PaymentSection>
  )
}

interface PaymentAmountDetailsProps {
  minimumDepositAmount: number
  depositAmount?: number
  fullAmount?: number
  dueDate?: string
  fullPaymentDueDate?: string
  amountType?: AmountType
}

function PaymentAmountDetails({
  minimumDepositAmount,
  depositAmount,
  fullAmount,
  dueDate,
  fullPaymentDueDate,
  amountType,
}: PaymentAmountDetailsProps) {
  return (
    <div className={styles.paymentAmountDetails}>
      <Divider />
      {depositAmount !== undefined && (
        <RemainingDepositRow
          remainingDeposit={depositAmount - minimumDepositAmount}
          dueDate={dueDate}
          amountType={amountType}
        />
      )}
      {fullAmount !== undefined && (
        <FullPaymentReminderRow
          remainingTotalAmount={fullAmount - minimumDepositAmount}
          fullPaymentDueDate={fullPaymentDueDate}
        />
      )}
    </div>
  )
}
